import React, { useEffect, useMemo, useState } from 'react'
import {
  getAllOrders,
  updateOrderStatus,
  deleteOrder,
} from '../services/orderService'
import {
  Avatar,
  Banner,
  Button,
  DetailList,
  Drawer,
  EmptyState,
  IconButton,
  Pagination,
  Pill,
  RowActions,
  SearchInput,
  SelectFilter,
  SkeletonRows,
  StatTiles,
  Tabs,
  Thumb,
  Toast,
  capitalize,
  downloadCsv,
  formatDate,
  formatOrderId,
  formatRWF,
  timeAgo,
  useConfirm,
  useFlash,
  usePagination,
} from './AdminUI'

const ORDER_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled']
const PAYMENT_STATUSES = ['pending', 'paid', 'failed']
const FLOW = ['pending', 'processing', 'shipped', 'delivered']

const STATUS_TONE = {
  pending: 'amber',
  processing: 'blue',
  shipped: 'indigo',
  delivered: 'green',
  cancelled: 'red',
}

const PAYMENT_TONE = { pending: 'amber', paid: 'green', failed: 'red' }

const METHOD_LABEL = { MTN: 'MTN MoMo', AIRTEL: 'Airtel Money', CARD: 'Card' }

const formatPaymentAmount = (payment) =>
  payment.currency === 'RWF'
    ? formatRWF(payment.amount)
    : `${payment.currency} ${Number(payment.amount || 0).toFixed(2)}`

const customerName = (order) => order.customerName || order.user?.name || 'Guest customer'
const customerEmail = (order) => order.customerEmail || order.user?.email || ''

function OrderTimeline({ status }) {
  if (status === 'cancelled') {
    return (
      <Banner tone="error">This order was cancelled. Stock for its items has been released.</Banner>
    )
  }
  const current = Math.max(0, FLOW.indexOf(status))
  return (
    <ol className="ax-steps">
      {FLOW.map((step, index) => (
        <li
          key={step}
          className={`ax-steps__item ${index < current ? 'is-done' : ''} ${index === current ? 'is-current' : ''}`}
        >
          <span className="ax-steps__dot">
            {index < current ? <i className="fas fa-check" aria-hidden="true" /> : index + 1}
          </span>
          <span className="ax-steps__label">{capitalize(step)}</span>
        </li>
      ))}
    </ol>
  )
}

function OrdersManagement() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useFlash()
  const [confirm, confirmDialog] = useConfirm()
  const [statusFilter, setStatusFilter] = useState('all')
  const [paymentFilter, setPaymentFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [selectedId, setSelectedId] = useState(null)
  const [saving, setSaving] = useState(false)

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null

  const loadOrders = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAllOrders(token)
      setOrders(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message || 'Failed to load orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) || null,
    [orders, selectedId]
  )

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(ORDER_STATUSES.map((s) => [s, 0]))
    let paidRevenue = 0
    let awaiting = 0
    orders.forEach((o) => {
      if (byStatus[o.status] !== undefined) byStatus[o.status] += 1
      if (o.paymentStatus === 'paid') paidRevenue += Number(o.total) || 0
      if (o.paymentStatus === 'pending') awaiting += 1
    })
    return {
      byStatus,
      paidRevenue,
      awaiting,
      open: byStatus.pending + byStatus.processing,
    }
  }, [orders])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = orders.filter((order) => {
      if (statusFilter === 'open' && !['pending', 'processing'].includes(order.status)) return false
      if (!['all', 'open'].includes(statusFilter) && order.status !== statusFilter) return false
      if (paymentFilter !== 'all' && order.paymentStatus !== paymentFilter) return false
      if (!q) return true
      return [
        order.id,
        order.phone,
        order.customerName,
        order.customerEmail,
        order.user?.name,
        order.user?.email,
        order.shippingAddress,
        ...(order.payments || []).map((p) => p.gatewayOrderId),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
    const sorters = {
      newest: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      oldest: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      'total-high': (a, b) => (Number(b.total) || 0) - (Number(a.total) || 0),
      'total-low': (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
    }
    return [...list].sort(sorters[sortBy])
  }, [orders, statusFilter, paymentFilter, search, sortBy])

  const pager = usePagination(filtered, 10, `${statusFilter}|${paymentFilter}|${search}|${sortBy}`)

  const openOrder = (order) => {
    setSelectedId(order.id)
    setError('')
  }

  const mergeOrder = (orderId, updated) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...updated } : o)))
  }

  const handleStatusChange = async (orderId, status) => {
    setSaving(true)
    setError('')
    try {
      const updated = await updateOrderStatus(orderId, status, token)
      mergeOrder(orderId, updated)
      setSuccess(`Order marked as ${status}`)
    } catch (err) {
      setError(err.message || 'Failed to update status')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteOrder = (order) => {
    const label = formatOrderId(order.id)
    const itemCount = (order.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0)
    return confirm({
      title: `Delete order #${label}?`,
      message: 'The order will be permanently removed from your records.',
      item: {
        media: <Avatar name={customerName(order)} size="lg" tone="soft" />,
        title: customerName(order),
        meta: `${itemCount} item${itemCount === 1 ? '' : 's'} · ${formatRWF(order.total)} · ${formatDate(order.createdAt)}`,
      },
      points: [
        'This action cannot be undone.',
        'Product stock from this order will be restored.',
        ...(order.paymentStatus === 'paid' ? ['This order is paid — refund the customer separately if needed.'] : []),
      ],
      confirmLabel: 'Delete order',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        setSaving(true)
        setError('')
        try {
          await deleteOrder(order.id, token)
          setOrders((prev) => prev.filter((o) => o.id !== order.id))
          setSelectedId((prev) => (prev === order.id ? null : prev))
          setSuccess(`Order #${label} deleted`)
        } catch (err) {
          setError(err.message || 'Failed to delete order')
        } finally {
          setSaving(false)
        }
      },
    })
  }

  const exportCsv = () => {
    const rows = [
      ['Order', 'Date', 'Customer', 'Email', 'Phone', 'Items', 'Total (RWF)', 'Payment', 'Payment method', 'Status', 'Delivery'],
      ...filtered.map((o) => [
        formatOrderId(o.id),
        formatDate(o.createdAt, true),
        customerName(o),
        customerEmail(o),
        o.phone || '',
        (o.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0),
        Number(o.total) || 0,
        o.paymentStatus,
        o.paymentMethod || '',
        o.status,
        o.deliveryMethod === 'store' ? 'Store pickup' : 'Delivery',
      ]),
    ]
    downloadCsv(`fabritech-orders-${new Date().toISOString().slice(0, 10)}.csv`, rows)
  }

  const statusTabs = [
    { id: 'all', label: 'All', count: orders.length },
    ...ORDER_STATUSES.map((s) => ({ id: s, label: capitalize(s), count: counts.byStatus[s] })),
  ]

  const stats = [
    {
      id: 'all',
      label: 'Total orders',
      value: orders.length.toLocaleString(),
      hint: 'All time',
      icon: 'fa-receipt',
      tone: 'blue',
      filter: 'all',
    },
    {
      id: 'open',
      label: 'To fulfil',
      value: counts.open,
      hint: 'Pending or processing',
      icon: 'fa-truck-fast',
      tone: 'indigo',
      filter: 'open',
    },
    {
      id: 'awaiting',
      label: 'Awaiting payment',
      value: counts.awaiting,
      hint: 'Not paid online yet',
      icon: 'fa-hourglass-half',
      tone: 'amber',
      filter: 'pay-pending',
    },
    {
      id: 'revenue',
      label: 'Paid revenue',
      value: formatRWF(counts.paidRevenue),
      hint: `${counts.byStatus.delivered} delivered`,
      icon: 'fa-coins',
      tone: 'green',
    },
  ]

  const hasFilters = statusFilter !== 'all' || paymentFilter !== 'all' || search

  return (
    <div className="ax">
      <StatTiles
        items={stats}
        active={paymentFilter === 'pending' ? 'pay-pending' : paymentFilter === 'all' ? statusFilter : ''}
        onSelect={(filter) => {
          if (filter === 'pay-pending') {
            setStatusFilter('all')
            setPaymentFilter('pending')
            return
          }
          setStatusFilter(filter)
          setPaymentFilter('all')
        }}
      />

      {counts.awaiting > 0 && paymentFilter !== 'pending' ? (
        <Banner tone="warning">
          {counts.awaiting} order{counts.awaiting === 1 ? ' is' : 's are'} waiting for payment confirmation.{' '}
          <button type="button" className="ax-inline-link" onClick={() => setPaymentFilter('pending')}>
            Review now
          </button>
        </Banner>
      ) : null}

      {error && !selected ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

      <div className="ax-card">
        <div className="ax-toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search order, customer, phone…" label="Search orders" />
          <SelectFilter
            value={paymentFilter}
            onChange={setPaymentFilter}
            icon="fa-wallet"
            label="Payment status"
            options={[
              { value: 'all', label: 'All payments' },
              ...PAYMENT_STATUSES.map((s) => ({ value: s, label: capitalize(s) })),
            ]}
          />
          <SelectFilter
            value={sortBy}
            onChange={setSortBy}
            icon="fa-arrow-down-wide-short"
            label="Sort orders"
            options={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
              { value: 'total-high', label: 'Highest total' },
              { value: 'total-low', label: 'Lowest total' },
            ]}
          />
          <span className="ax-toolbar__grow" />
          {hasFilters ? (
            <Button
              variant="ghost"
              icon="fa-filter-circle-xmark"
              onClick={() => {
                setStatusFilter('all')
                setPaymentFilter('all')
                setSearch('')
              }}
            >
              Clear
            </Button>
          ) : null}
          <IconButton icon={loading ? 'fa-spinner fa-spin' : 'fa-rotate'} label="Refresh orders" className="ax-icon-btn--outline" onClick={loadOrders} disabled={loading} />
          <Button icon="fa-file-arrow-down" onClick={exportCsv} disabled={!filtered.length}>
            Export
          </Button>
        </div>
        <div className="ax-toolbar ax-toolbar--sub">
          <Tabs options={statusTabs} value={statusFilter === 'open' ? '' : statusFilter} onChange={setStatusFilter} label="Order status" />
          {statusFilter === 'open' ? <Pill tone="indigo">Showing pending + processing</Pill> : null}
        </div>

        {loading && orders.length === 0 ? (
          <SkeletonRows cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="fa-receipt"
            title={hasFilters ? 'No orders match these filters' : 'No orders yet'}
            text={hasFilters ? 'Try a different status, payment filter or search term.' : 'Orders placed at checkout will appear here.'}
          />
        ) : (
          <>
            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th className="is-right">Total</th>
                    <th className="is-right">
                      <span className="ax-sr">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageItems.map((order) => {
                    const items = order.items || []
                    const units = items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0)
                    return (
                      <tr key={order.id} className="is-clickable" onClick={() => openOrder(order)}>
                        <td>
                          <div className="ax-cell__text">
                            <strong className="ax-mono">#{formatOrderId(order.id)}</strong>
                            <small>
                              {units} unit{units === 1 ? '' : 's'} ·{' '}
                              {order.deliveryMethod === 'store' ? 'Pickup' : 'Delivery'}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="ax-cell">
                            <Avatar name={customerName(order)} size="sm" tone="soft" />
                            <div className="ax-cell__text">
                              <strong>{customerName(order)}</strong>
                              <small>{order.phone || customerEmail(order) || '—'}</small>
                            </div>
                          </div>
                        </td>
                        <td className="is-nowrap">
                          <span className="ax-strong">{formatDate(order.createdAt)}</span>
                          <span className="ax-sub">{timeAgo(order.createdAt)}</span>
                        </td>
                        <td>
                          <Pill tone={PAYMENT_TONE[order.paymentStatus] || 'gray'}>{order.paymentStatus || 'pending'}</Pill>
                          <span className="ax-sub">{order.paymentMethod || '—'}</span>
                        </td>
                        <td>
                          <Pill tone={STATUS_TONE[order.status] || 'gray'}>{order.status || 'pending'}</Pill>
                        </td>
                        <td className="is-right is-nowrap">
                          <span className="ax-strong">{formatRWF(order.total)}</span>
                        </td>
                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                          <RowActions
                            label={`Actions for order #${formatOrderId(order.id)}`}
                            title={`Order #${formatOrderId(order.id)}`}
                            subtitle={`${customerName(order)} · ${formatRWF(order.total)}`}
                            media={<Avatar name={customerName(order)} size="lg" tone="soft" />}
                            actions={[
                              {
                                id: 'view',
                                label: 'View order details',
                                description: 'Items, customer, delivery and payment attempts',
                                icon: 'fa-eye',
                                onClick: () => openOrder(order),
                              },
                              customerEmail(order)
                                ? {
                                    id: 'email',
                                    label: 'Email customer',
                                    description: customerEmail(order),
                                    icon: 'fa-envelope',
                                    onClick: () => {
                                      window.location.href = `mailto:${customerEmail(order)}?subject=${encodeURIComponent(
                                        `Your Fabritech order #${formatOrderId(order.id)}`
                                      )}`
                                    },
                                  }
                                : null,
                              order.phone
                                ? {
                                    id: 'call',
                                    label: 'Call customer',
                                    description: order.phone,
                                    icon: 'fa-phone',
                                    onClick: () => {
                                      window.location.href = `tel:${order.phone}`
                                    },
                                  }
                                : null,
                              {
                                id: 'delete',
                                label: 'Delete order',
                                description: 'Cannot be undone · stock will be restored',
                                icon: 'fa-trash-can',
                                tone: 'danger',
                                disabled: saving,
                                onClick: () => handleDeleteOrder(order),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination pager={pager} label="orders" />
          </>
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        width={620}
        title={selected ? `Order #${formatOrderId(selected.id)}` : ''}
        subtitle={selected ? `Placed ${formatDate(selected.createdAt, true)} · ${timeAgo(selected.createdAt)}` : ''}
        headerExtra={
          selected ? (
            <>
              <Pill tone={PAYMENT_TONE[selected.paymentStatus] || 'gray'}>{selected.paymentStatus}</Pill>
              <Pill tone={STATUS_TONE[selected.status] || 'gray'}>{selected.status}</Pill>
            </>
          ) : null
        }
        footer={
          selected ? (
            <>
              <Button variant="danger" icon="fa-trash-can" disabled={saving} onClick={() => handleDeleteOrder(selected)}>
                Delete
              </Button>
              <span className="ax-foot__spacer" />
              <Button onClick={() => setSelectedId(null)}>Close</Button>
            </>
          ) : null
        }
      >
        {selected ? (
          <>
            {error ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

            <OrderTimeline status={selected.status} />

            <div className="ax-panel">
              <div className="ax-panel__head">
                <h3>
                  <i className="fas fa-sliders" aria-hidden="true" /> Update order
                </h3>
              </div>
              <div className="ax-panel__body ax-form-grid">
                <div className="ax-field">
                  <label className="ax-field__label" htmlFor="om-status">Fulfilment status</label>
                  <select
                    id="om-status"
                    className="ax-input"
                    value={selected.status}
                    disabled={saving}
                    onChange={(e) => handleStatusChange(selected.id, e.target.value)}
                  >
                    {ORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {capitalize(s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="ax-field">
                  <span className="ax-field__label">Payment status</span>
                  <div>
                    <Pill tone={PAYMENT_TONE[selected.paymentStatus] || 'gray'}>{selected.paymentStatus || 'pending'}</Pill>
                  </div>
                  <small className="ax-field__hint">Set automatically by the payment provider</small>
                </div>
              </div>
            </div>

            <div className="ax-kv-grid">
              <div className="ax-panel">
                <div className="ax-panel__head">
                  <h3>
                    <i className="fas fa-user" aria-hidden="true" /> Customer
                  </h3>
                </div>
                <div className="ax-panel__body ax-contact">
                  <Avatar name={customerName(selected)} size="lg" />
                  <div className="ax-cell__text">
                    <strong>{customerName(selected)}</strong>
                    {customerEmail(selected) ? (
                      <a href={`mailto:${customerEmail(selected)}`} className="ax-link">
                        {customerEmail(selected)}
                      </a>
                    ) : null}
                    {selected.phone ? (
                      <a href={`tel:${selected.phone}`} className="ax-link">
                        {selected.phone}
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="ax-panel">
                <div className="ax-panel__head">
                  <h3>
                    <i className={`fas ${selected.deliveryMethod === 'store' ? 'fa-store' : 'fa-truck'}`} aria-hidden="true" />{' '}
                    {selected.deliveryMethod === 'store' ? 'Store pickup' : 'Delivery'}
                  </h3>
                </div>
                <div className="ax-panel__body">
                  <p className="ax-body-text">
                    {typeof selected.shippingAddress === 'string' && selected.shippingAddress
                      ? selected.shippingAddress
                      : 'No address provided'}
                  </p>
                </div>
              </div>
            </div>

            <div className="ax-panel">
              <div className="ax-panel__head">
                <h3>
                  <i className="fas fa-box" aria-hidden="true" /> Items
                </h3>
                <span className="ax-sub">{(selected.items || []).length} line{(selected.items || []).length === 1 ? '' : 's'}</span>
              </div>
              <ul className="ax-lines">
                {(selected.items || []).map((item, idx) => (
                  <li key={item._id || item.id || idx} className="ax-lines__item">
                    <Thumb src={item.image || item.product?.image} size="sm" />
                    <div className="ax-cell__text">
                      <strong>{item.name || item.title || item.product?.title}</strong>
                      <small>
                        {formatRWF(item.price)} × {item.quantity || 1}
                      </small>
                    </div>
                    <span className="ax-strong">{formatRWF((item.price || 0) * (item.quantity || 1))}</span>
                  </li>
                ))}
              </ul>
              <div className="ax-totals">
                <div>
                  <span>Subtotal</span>
                  <span>{formatRWF(selected.subtotal)}</span>
                </div>
                {/* Only older orders were charged VAT on top of the prices */}
                {selected.tax > 0 && (
                  <div>
                    <span>VAT (18%)</span>
                    <span>{formatRWF(selected.tax)}</span>
                  </div>
                )}
                <div>
                  <span>Shipping</span>
                  <span>{(selected.shippingFee || 0) === 0 ? 'Free' : formatRWF(selected.shippingFee)}</span>
                </div>
                <div className="is-grand">
                  <span>Total</span>
                  <strong>{formatRWF(selected.total)}</strong>
                </div>
              </div>
            </div>

            <div className="ax-panel">
              <div className="ax-panel__head">
                <h3>
                  <i className="fas fa-wallet" aria-hidden="true" /> Payment
                </h3>
                <Pill tone={PAYMENT_TONE[selected.paymentStatus] || 'gray'}>{selected.paymentStatus}</Pill>
              </div>
              <div className="ax-panel__body ax-stack">
                <DetailList
                  rows={[
                    {
                      label: 'Method',
                      value: `${selected.paymentMethod || '—'}${selected.momoProvider ? ` · ${String(selected.momoProvider).toUpperCase()}` : ''}`,
                    },
                    selected.notes ? { label: 'Notes', value: selected.notes } : null,
                  ]}
                />

                {(selected.payments || []).length ? (
                  <ul className="ax-lines">
                    {selected.payments.map((payment) => (
                      <li key={payment.id} className="ax-lines__item">
                        <span className="ax-file__icon">
                          <i
                            className={`fas ${payment.method === 'CARD' ? 'fa-credit-card' : 'fa-mobile-screen-button'}`}
                            aria-hidden="true"
                          />
                        </span>
                        <div className="ax-cell__text">
                          <strong>
                            {METHOD_LABEL[payment.method] || payment.method} · {formatPaymentAmount(payment)}
                          </strong>
                          <small>
                            {formatDate(payment.createdAt, true)}
                            {payment.phone ? ` · ${payment.phone}` : ''}
                            {payment.gatewayOrderId ? ` · Ref ${payment.gatewayOrderId}` : ''}
                          </small>
                          {payment.errorMessage && payment.status !== 'paid' ? <small>{payment.errorMessage}</small> : null}
                        </div>
                        <Pill tone={PAYMENT_TONE[payment.status] || 'gray'}>{payment.status}</Pill>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="ax-sub">No online payment has been started for this order yet.</p>
                )}

                {selected.paymentStatus !== 'paid' ? (
                  <div className="ax-callout ax-callout--blue">
                    <i className="fas fa-circle-info" aria-hidden="true" />
                    The payment status is updated automatically when the customer pays with MTN, Airtel or card.
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </Drawer>

      <Toast message={success} />
      {confirmDialog}
    </div>
  )
}

export default OrdersManagement
